import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { CircularProgress } from "@mui/material";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useSelector } from "react-redux";
import CalComDialog from "../calComDialog/calComDialog.jsx";
import i18n from "../../../../utils/i18n";
import classes from "./bookingSlots.module.scss";

const CUSTOMER_AGENTS = [
  {
    name: "Nina",
    organizerId: 878505,
    eventTypeId: 962905,
    directEventUrl:
      "https://cal.com/team/bookkeepie-magyarorszag/bookkeepie-ugyintezes-idopontfoglalas-delelott",
  },
  // {
  //   name: "Orsi",
  //   organizerId: 878507,
  //   eventTypeId: 1338953,
  //   directEventUrl:
  //     "https://cal.com/team/bookkeepie-magyarorszag/delutani-bookkeepie-ugyintezes-idopontfoglalas",
  // },
  {
    name: "Andi",
    organizerId: 1466315,
    eventTypeId: 2225788,
    directEventUrl:
      "https://cal.com/team/bookkeepie-magyarorszag/egesz-napos-bookkeepie-ugyintezes-idopont-foglalas",
  },
];

const BUDAPEST_TIMEZONE = "Europe/Budapest";
const DAYS_AHEAD = 14;
const DAY_TILE_COUNT = 3;

const toIso = (date) => date.toISOString().replace(".000", "");

const capitalizeHu = (value) => {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
};

const formatCompanyPhoneForCalCom = (rawPhone) => {
  const companyPhone = String(rawPhone || "").trim();
  if (!companyPhone) {
    return "";
  }

  return companyPhone.startsWith("+") ? companyPhone : `+${companyPhone}`;
};

const getBudapestDayKey = (dateInput) => {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: BUDAPEST_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(dateInput));
};

const addDaysToDayKey = (dayKey, days) => {
  const date = new Date(`${dayKey}T12:00:00`);
  date.setDate(date.getDate() + days);
  return getBudapestDayKey(date);
};

const pickRandomItem = (items) => items[Math.floor(Math.random() * items.length)];

const fetchAgentSlots = async (agent, startTime, endTime) => {
  const params = new URLSearchParams({
    eventTypeId: String(agent.eventTypeId),
    startTime: toIso(startTime),
    endTime: toIso(endTime),
    timeZone: BUDAPEST_TIMEZONE,
  });

  const response = await fetch(
    `https://api.cal.com/v2/slots/available?${params.toString()}`,
  );

  if (!response.ok) {
    throw new Error(`Cal.com slot request failed for ${agent.name}`);
  }

  const payload = await response.json();
  return payload?.data?.slots || {};
};

const mergeSlotsByDay = (agentSlotResults) => {
  const dayMap = new Map();

  agentSlotResults.forEach(({ agent, groupedSlots }) => {
    Object.entries(groupedSlots).forEach(([dayKey, daySlots]) => {
      const slotTimes = (Array.isArray(daySlots) ? daySlots : [])
        .map((slot) => slot?.time)
        .filter(Boolean)
        .sort((a, b) => new Date(a) - new Date(b));

      if (slotTimes.length === 0) {
        return;
      }

      const normalizedDayKey = getBudapestDayKey(slotTimes[0]);
      const existing = dayMap.get(normalizedDayKey) || {
        agents: [],
        slotsByAgent: new Map(),
      };

      if (!existing.agents.some((entry) => entry.eventTypeId === agent.eventTypeId)) {
        existing.agents.push(agent);
      }

      const previousSlots = existing.slotsByAgent.get(agent) || [];
      existing.slotsByAgent.set(
        agent,
        [...previousSlots, ...slotTimes].sort((a, b) => new Date(a) - new Date(b)),
      );
      dayMap.set(normalizedDayKey, existing);
    });
  });

  return [...dayMap.entries()]
    .sort(([dayA], [dayB]) => dayA.localeCompare(dayB))
    .slice(0, DAY_TILE_COUNT)
    .map(([dayKey, dayData], tileIndex) => ({
      dayKey,
      tileIndex,
      agents: dayData.agents,
      slotsByAgent: dayData.slotsByAgent,
    }));
};

const getDayCardTitle = (dayKey, tileIndex) => {
  if (tileIndex === 2) {
    return i18n.t("searchflow.othertimeslot");
  }

  const todayKey = getBudapestDayKey(new Date());
  const tomorrowKey = addDaysToDayKey(todayKey, 1);

  if (dayKey === todayKey) {
    return i18n.t("searchflow.today");
  }
  if (dayKey === tomorrowKey) {
    return i18n.t("searchflow.tomorrow");
  }

  const weekday = new Intl.DateTimeFormat("hu-HU", {
    timeZone: BUDAPEST_TIMEZONE,
    weekday: "long",
  }).format(new Date(`${dayKey}T12:00:00`));

  return capitalizeHu(weekday);
};

const getDayCardSubtitle = (dayKey, tileIndex) => {
  if (tileIndex === 2) {
    return i18n.t("searchflow.customtimeslot");
  }

  const formatted = new Intl.DateTimeFormat("hu-HU", {
    timeZone: BUDAPEST_TIMEZONE,
    month: "long",
    day: "numeric",
  }).format(new Date(`${dayKey}T12:00:00`));

  const dateLabel = capitalizeHu(formatted).replace(/\.$/, "");
  return `${dateLabel}.`;
};

const BookingSlots = (props, ref) => {
  const [dayTiles, setDayTiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogUrl, setDialogUrl] = useState("");
  const reqID = useSelector((state) => state.company_store?.reqID || "");
  const contactEmail = useSelector((state) => state.company_store?.email || "");
  const companyCtcName = useSelector(
    (state) => state.company_store?.companyctcname || "",
  );
  const companyPhone = useSelector(
    (state) => state.company_store?.companyPhone || "",
  );
  const reqMessage = useSelector((state) => state.company_store?.reqMessage || "");

  useEffect(() => {
    let isActive = true;

    const loadSlots = async () => {
      try {
        setLoading(true);
        setError(false);

        const startTime = new Date();
        const endTime = new Date(startTime);
        endTime.setDate(endTime.getDate() + DAYS_AHEAD);

        const agentSlotResults = await Promise.all(
          CUSTOMER_AGENTS.map(async (agent) => ({
            agent,
            groupedSlots: await fetchAgentSlots(agent, startTime, endTime),
          })),
        );

        if (!isActive) {
          return;
        }

        setDayTiles(mergeSlotsByDay(agentSlotResults));
      } catch (err) {
        console.error("Failed to load customer agent slots:", err);
        if (isActive) {
          setError(true);
          setDayTiles([]);
        }
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    loadSlots();

    return () => {
      isActive = false;
    };
  }, []);

  const openDialogWithUrl = (baseUrl, slotIsoString = null) => {
    const url = new URL(baseUrl);

    if (slotIsoString) {
      url.searchParams.set("date", slotIsoString);
    }

    if (reqID) {
      url.searchParams.set("reqID", reqID);
    }

    if (contactEmail) {
      url.searchParams.set("email", contactEmail);
    }

    if (companyCtcName) {
      url.searchParams.set("name", companyCtcName);
    }

    const formattedPhone = formatCompanyPhoneForCalCom(companyPhone);
    if (formattedPhone) {
      url.searchParams.set(
        "location",
        JSON.stringify({
          value: "phone",
          optionValue: formattedPhone,
        }),
      );
    }

    if (reqID) {
      url.searchParams.set(
        "metadata",
        JSON.stringify({
          reqID,
          name: companyCtcName,
          email: contactEmail,
          phone: formattedPhone,
        }),
      );
    }

    if (reqMessage) {
      url.searchParams.set("notes", reqMessage);
    }

    setDialogUrl(url.toString());
    setIsDialogOpen(true);
  };

  const openBookingForDay = (dayTile) => {
    const selectedAgent = pickRandomItem(dayTile.agents);
    const agentSlots = dayTile.slotsByAgent.get(selectedAgent) || [];
    const slotIsoString = agentSlots[0] || `${dayTile.dayKey}T09:00:00.000Z`;

    openDialogWithUrl(selectedAgent.directEventUrl, slotIsoString);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
  };

  // Lets a parent step's "Next" button open booking directly (e.g. when the
  // user picked the consultation option but hasn't clicked a day tile yet),
  // without duplicating the day/agent/slot-picking logic above.
  useImperativeHandle(ref, () => ({
    openDefaultBooking: () => {
      if (dayTiles.length > 0) {
        openBookingForDay(dayTiles[0]);
        return;
      }
      // Slots may still be loading (or failed to load) — fall back to a
      // generic booking link immediately rather than silently doing nothing.
      openDialogWithUrl(CUSTOMER_AGENTS[0].directEventUrl);
    },
  }));

  return (
    <div className={classes.root}>
      {loading ? (
        <div className={classes.loadingRow}>
          <CircularProgress size={18} sx={{ color: "#1c32f3" }} />
          <span>{i18n.t("searchflow.loadingslots")}</span>
        </div>
      ) : null}

      {!loading && dayTiles.length > 0 ? (
        <div className={classes.cardList}>
          {dayTiles.map((dayTile) => (
            <button
              key={dayTile.dayKey}
              type="button"
              className={classes.dayCard}
              onClick={() => openBookingForDay(dayTile)}
            >
              <div className={classes.cardText}>
                <span className={classes.cardTitle}>
                  {getDayCardTitle(dayTile.dayKey, dayTile.tileIndex)}
                </span>
                <span className={classes.cardSubtitle}>
                  {getDayCardSubtitle(dayTile.dayKey, dayTile.tileIndex)}
                </span>
              </div>
              <ChevronRightIcon className={classes.chevron} aria-hidden="true" />
            </button>
          ))}
        </div>
      ) : null}

      {!loading && (error || dayTiles.length === 0) ? (
        <p className={classes.errorText}>{i18n.t("searchflow.slotsloaderror")}</p>
      ) : null}

      <CalComDialog
        open={isDialogOpen}
        onClose={handleDialogClose}
        url={dialogUrl}
      />
    </div>
  );
};

export default forwardRef(BookingSlots);
